/*@flow*/
import type { Node } from 'react';
import React, { useEffect, createContext, useReducer, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';
import nested from 'lib/nested';
import resourceTypes_ from 'lib/resource';
import { css } from '@emotion/core';
import ColorBox from 'comps/ColorBox';
import titleize from 'underscore.string/titleize';
import humanize from 'underscore.string/humanize';
import * as I from 'immutable';
import { pascalize } from 'humps';
import * as t from 'lib/t';
import setIn from 'lib/setIn';
import Present from 'lib/Present';
import Blank from 'lib/Blank';
import has from 'lib/hasProperty';
import hash from 'lib/hash';
import cx from 'classnames';
import GetStartupData from './queries/GetStartupData';
import CreateMarkerColors from './mutations/CreateMarkerColors';
import UpdateCompanyDefaultStrokeColor from './mutations/UpdateCompanyDefaultStrokeColor';
import useCachedQuery from 'lib/useCachedQuery';
import { ltButtonBarBg } from "../Left2/colors";

let ReducerContext;

const checkMutationStatus = (res) => {
  const success = nested(res, 'data.markerColorsCreate.success', false);
  const msg = ['Network error.'];
  const errors = nested(res, 'data.markerColorsCreate.errors', msg);
  if (success) {
    return true;
  }
  const err = new Error();
  // $FlowFixMe[prop-missing]
  err.errors = errors;
  throw err;
};

const StyleCell = (props: { enabled?: boolean, children?: React$Node }) => (
  <div
    className={cx({ enabled: props.enabled, disabled: !props.enabled })}
    css={css(`
      display: grid;
      align-items: center;
      height: 25px;
      ${props.enabled ? '' : 'opacity: 0.5;'}
    `)}
  >
    {props.children}
  </div>
);

const alertCss = css(`
  background: pink;
  padding: 5px 10px;
  color: #d9534f;
  a { display: block; text-align: right; color: #333; text-decoration: underline; }
`);

const gridCss = css(`
  display: grid;
  grid-gap: 10px;
  width: 100%;

  background: ${ltButtonBarBg};
  color: #333;
  padding: 10px;
  padding-right: 15px;
  margin-top: 5px;
`);

const rowCss = css(`
  display: grid;
  grid-template-columns: 88px 1fr 20px;
  align-items: center;
  grid-gap: 10px;
  select { width: 100%; color: black; }

  & > div:nth-of-type(1) {
    text-align: right;
  }
`);

const actionRowCss = css(`
  span {
    color: #efefef;
    margin-right: 15px;
  }
  a {
    color: white;
    margin-right: 15px;
    &:hover {
      color: white;
    }
    &:nth-of-type(1) {
      font-weight: bold;
    }
  }
`);

const categoryPaths: { [key: string]: string } = {
  StreetscapeAsset: 'me.company.streetscapeAssetTypes',
  StreetscapeIssue: 'me.company.streetscapeIssueCategories',
  Tenant: 'me.company.tenantCategories',
  Attraction: 'me.company.attractionCategories',
  Event: 'me.company.eventCategories',
  Building: 'me.company.buildingCategories',
  Unit: 'me.company.unitCategories',
  Listing: 'me.company.listingCategories',
  Property: 'me.company.propertyCategories',
  Organization: 'me.company.organizationCategories',
};

const statusPaths: { [key: string]: string } = {
  StreetscapeAsset: 'me.company.streetscapeAssetStatuses',
  StreetscapeIssue: 'me.company.streetscapeIssueStatuses',
  Tenant: 'me.company.tenantStatuses',
  Attraction: 'me.company.attractionStatuses',
  Event: 'me.company.eventStatuses',
  Building: 'me.company.buildingStatuses',
  Property: 'me.company.propertyStatuses',
  Unit: 'me.company.unitStatuses',
  Listing: 'me.company.listingStatuses',
  Development: 'me.company.developmentStatuses',
  Organization: 'me.company.organizationStatuses',
};

const resourceTypes = {
  ...resourceTypes_,
  listing: resourceTypes_.listing.default,
};
const resourceNames = Object.keys(resourceTypes)
  .map((v) => pascalize(humanize(v)))
  .sort();

const recordTypeOptions = resourceNames.map((v) => [
  pascalize(humanize(v)),
  titleize(humanize(v)),
]);

const initialState = I.Record({
  recordType: '',
  field: '',
  fieldValue: '',
  visual: 'fill',
  color: '',
  savedColorFill: '',
  savedColorStroke: '',
  saveStatus: 'READY', // CHANGES_PRESENT | SAVING
  errors: [],
});

const mkState = (state) => initialState(state || {});

type State = {
  recordType: string,
  field: string,
  fieldValue: any,
  visual: 'stroke' | 'fill',
  color: string,
  savedColorFill: string,
  savedColorStroke: string,
  saveStatus: 'READY' | 'CHANGES_PRESENT' | 'SAVING',
  errors: string[],
};

function reducer(state, action) {
  let newState = state;

  switch (action.type) {
    case 'SET_RECORD':
      newState = setIn(newState)(action.payload);
      break;
    case 'UPDATE_RECORD_TYPE':
      newState = newState
        .set('recordType', action.payload)
        .set('field', '')
        .set('fieldValue', '')
        .set('saveStatus', 'READY')
        .set('color', '')
        .set('visual', 'fill')
        .set('savedColorFill', '')
        .set('savedColorStroke', '');
      break;
    case 'UPDATE_FIELD':
      newState = newState
        .set('field', action.payload)
        .set('fieldValue', '')
        .set('saveStatus', 'READY')
        .set('color', '')
        .set('visual', 'fill')
        .set('savedColorFill', '')
        .set('savedColorStroke', '');
      break;
    case 'UPDATE_FIELD_VALUE':
      newState = newState
        .set('fieldValue', action.payload)
        .set('saveStatus', 'READY')
        .set('color', '')
        .set('visual', 'fill')
        .set('savedColorFill', '')
        .set('savedColorStroke', '');
      break;
    case 'UPDATE_VISUAL':
      newState = newState
        .set('visual', action.payload)
        .set('saveStatus', 'READY')
        .set('color', '')
        .set('savedColorFill', '')
        .set('savedColorStroke', '');
      break;
    default:
      throw new Error();
  }

  return newState;
}

const commands = (ctx) => {
  return {
    set(path, value) {
      ctx.dispatch({
        type: 'SET_RECORD',
        payload: { path, value },
      });
    },
    updateRecordType(value) {
      ctx.dispatch({
        type: 'UPDATE_RECORD_TYPE',
        payload: value,
      });
    },
    updateField(value) {
      ctx.dispatch({
        type: 'UPDATE_FIELD',
        payload: value,
      });
    },
    updateFieldValue(value) {
      ctx.dispatch({
        type: 'UPDATE_FIELD_VALUE',
        payload: value,
      });
    },
    updateVisual(value) {
      ctx.dispatch({
        type: 'UPDATE_VISUAL',
        payload: value,
      });
    },
    reset() {
      commands(ctx).clearUnsavedColor();
      commands(ctx).set('saveStatus', 'READY');
      commands(ctx).set('errors', []);
    },
    saved() {
      commands(ctx).set('saveStatus', 'READY');
    },
    mutated() {
      commands(ctx).set('saveStatus', 'CHANGES_PRESENT');
    },
    mutate(key, value) {
      let { visual, color: colorValue } = ctx.state;
      visual = titleize(visual);
      commands(ctx).set(`savedColor${visual}`, colorValue);
      commands(ctx).set(key, value);
      commands(ctx).mutated();
    },
    initColor(color) {
      const visual = titleize(color.visual);
      const value = color.value;
      commands(ctx).set(`savedColor${visual}`, value);
      commands(ctx).set('color', value);
      commands(ctx).set('visual', color.visual);
    },
    clearColor() {
      commands(ctx).set('color', '');
    },
    clearSavedColor() {
      commands(ctx).clearColor();
      commands(ctx).mutated();
    },
    clearUnsavedColor() {
      const { savedColorFill, savedColorStroke, visual } = ctx.state;
      let color;
      if (visual === 'stroke') {
        color = savedColorStroke;
      } else if (visual === 'fill') {
        color = savedColorFill;
      }
      commands(ctx).set('color', color);
    },
  };
};

const createOption = ([value, label]) => (
  <option key={value} value={value}>
    {label}
  </option>
);

const RemoveColorButton = () => {
  const ctx = useContext(ReducerContext);
  if (Blank.is(ctx.state.color) || ctx.state.color === 'white') {
    return null;
  }

  return (
    <a
      className="RemoveColorButton"
      data-testid="RemoveColorButton"
      onClick={() => {
        commands(ctx).clearSavedColor();
      }}
      css={css('margin-left: 5px; color: white; &:hover { color: white; }')}
    >
      Remove color
    </a>
  );
};

const VISUALS = ['stroke', 'fill'];
const FieldValuePresent = t.subtype(Present, (v) => Present.is(v.fieldValue));
const FieldRecordType = t.subtype(Present, (v) => v.field === 'recordType');
const FieldBlank = t.subtype(Present, (v) => Blank.is(v.field));
const FieldValid = t.union([FieldBlank, FieldRecordType, FieldValuePresent]);
const RecordTypePresent = t.subtype(Present, (v) => Present.is(v.recordType));
const IsNotSaving = t.subtype(Present, (v) => v.saveStatus !== 'SAVING');
const VisualValid = t.subtype(Present, (v) => VISUALS.includes(v.visual));
const ColorEnabled = t.intersection([
  IsNotSaving,
  RecordTypePresent,
  FieldValid,
  VisualValid,
]);
const isColor = (v) => has(v, 'type') && has(v, 'value');
const Color = t.subtype(t.Object, isColor);

const getFieldOptions = (valueOptionMap) => {
  const presentValueOptions = Object.entries(valueOptionMap)
    .filter((arr) => Present.is(arr[1]))
    .map((arr) => [arr[0], arr[0]]);

  return Present.is(presentValueOptions)
    ? [['recordType', 'Record Type']]
        .concat(presentValueOptions)
        .map(([k, v]) => [k, titleize(humanize(v))])
    : [];
};

function DefaultStrokeColorCell({
  defaultStrokeColor,
  companyId,
  isLoading,
}: {
  defaultStrokeColor: string,
  companyId: string,
  isLoading: boolean,
}) {
  const [updateCompany] = useMutation(UpdateCompanyDefaultStrokeColor, {
    refetchQueries: [{ query: GetStartupData }],
  });
  type VarT = {
    id: ID,
    defaultStrokeColor: string,
  };

  function updateColor(color) {
    updateCompany({ variables: { id: companyId, defaultStrokeColor: color } });
  }
  return (
    <StyleCell enabled>
      <div
        css={css(`
          display: grid;
          align-items: center;
          grid-template-columns: 25px 1fr;
        `)}
      >
        <ColorBox
          enabled={!isLoading}
          testIdPrefix="DefaultStroke"
          value={defaultStrokeColor || 'white'}
          className="d-flex justify-content-around p-0"
          onChange={(c) => updateColor(c)}
        />{' '}
        <div className="d-flex align-content-center" />
      </div>
    </StyleCell>
  );
}

const Component = (props: { state?: State }): Node => {
  const [state, dispatch] = useReducer(reducer, mkState(props.state));
  const { data, errors, loading, refetch } = useCachedQuery(GetStartupData);
  const ctx = { state, dispatch };
  ReducerContext = createContext<any>(ctx);
  const defaultStrokeColor = nested(
    data,
    'me.company.defaultStrokeColor',
    'white'
  );
  const markerColors = nested(data, 'me.company.allMarkerColors', []);
  const categoryPath = categoryPaths[state.recordType] || '';
  const statusPath = statusPaths[state.recordType] || '';
  const categoryList = nested(data, categoryPath, []);
  const statusList = nested(data, statusPath, []);
  const companyId = nested(data, 'me.company.id');
  const valueOptionMap = {
    status: statusList.map((v) => [v, v]),
    category: categoryList.map((v) => [v, v]),
  };
  const fieldOptions = getFieldOptions(valueOptionMap);
  const isColorEnabled = ColorEnabled.is(state);

  useEffect(() => {
    Present.is(errors) && commands(ctx).set('errors', errors);
  }, [hash(errors)]);

  useEffect(() => {
    const isDefaultColor = (v) => Blank.is(v.status) && Blank.is(v.category);
    const DefaultColor = t.subtype(Color, isDefaultColor);
    const isCurrentField = (v) => {
      if (v.type !== state.recordType) return false;
      const val = state.fieldValue;
      if (state.field === 'status') return v.status === val;
      if (state.field === 'category') return v.category === val;
      return DefaultColor.is(v);
    };
    const CurrentField = t.subtype(Color, isCurrentField);
    const CurVisual = t.subtype(Color, (v) => v.visual === state.visual);
    const CurColor = t.intersection([CurVisual, CurrentField]);
    const color = markerColors.find(CurColor.is);
    const colorDefault = markerColors.find(DefaultColor.is);

    if (Present.is(color)) {
      commands(ctx).initColor(color);
    } else if (Present.is(colorDefault) && state.field === 'category') {
      if (isColorEnabled) {
        // When the field is a category, and there is no color for it,
        // use the default color.
        // commands(ctx).initColor(colorDefault);
      }
    }
  }, [
    hash(markerColors),
    state.recordType,
    state.field,
    state.fieldValue,
    state.visual,
    isColorEnabled,
  ]);

  const [mutateColor] = useMutation(CreateMarkerColors);
  type VarT = {
    type: string,
    value: string,
    visual: string,
    status?: ?string,
    category?: ?string,
  };
  const createColors = (vs: VarT[]) =>
    mutateColor({ variables: { companyId, markerColors: vs } });

  const isFieldEnabled = Present.is(fieldOptions);
  const fieldValueOptions = valueOptionMap[state.field] || [];
  const isValueEnabled = Present.is(fieldValueOptions);

  const onUpdate = () => {
    commands(ctx).set('saveStatus', 'SAVING');

    let vs = [];
    const getField = (k) => (state.field === k ? state.fieldValue : null);

    vs.push({
      type: state.recordType,
      value: state.color,
      status: getField('status'),
      category: getField('category'),
      visual: state.visual,
    });

    createColors(vs)
      .then(checkMutationStatus)
      .then(() => refetch())
      .then(() => {
        commands(ctx).saved();
      })
      .catch((e) => {
        const err = (e.errors || [e.message]).filter(Boolean);
        commands(ctx).set('errors', err);
        commands(ctx).saved();
      });
  };

  const hasErrors = Present.is(state.errors);
  const hasChanged = state.saveStatus === 'CHANGES_PRESENT';
  const isSaving = state.saveStatus === 'SAVING';
  const showActionRow = hasChanged || isSaving || hasErrors;

  return (
    <ReducerContext.Provider value={ctx}>
      <div css={gridCss}>
        <div className="SelectDefaultStrokeColor" css={rowCss}>
          <div>Stroke Color</div>
          <DefaultStrokeColorCell
            companyId={companyId}
            defaultStrokeColor={defaultStrokeColor}
            isLoading={loading}
          />
        </div>
        <div className="SelectRecordType" css={rowCss}>
          <div>Record Type</div>
          <div>
            <select
              data-testid="SelectRecordType"
              value={state.recordType}
              onChange={(e) => {
                const { value } = e.target;
                Present.is(value) && commands(ctx).updateRecordType(value);
              }}
            >
              <option value=""> -- Select -- </option>
              {recordTypeOptions.map(createOption)}
            </select>
          </div>
          <div />
        </div>
        {isFieldEnabled && (
          <div className="SelectField" css={rowCss}>
            <div>Field</div>
            <div>
              <select
                data-testid="SelectField"
                value={state.field}
                onChange={(e) => {
                  commands(ctx).updateField(e.target.value);
                }}
              >
                {fieldOptions.map(createOption)}
              </select>
            </div>
          </div>
        )}
        {isValueEnabled && (
          <div className="SelectFieldValue" css={rowCss}>
            <div>Value</div>
            <div>
              <select
                data-testid="SelectFieldValue"
                value={state.fieldValue}
                onChange={(e) => {
                  commands(ctx).updateFieldValue(e.target.value);
                }}
              >
                <option value=""> -- Select -- </option>
                {fieldValueOptions.map(createOption)}
              </select>
            </div>
          </div>
        )}
        <div className="SelectVisual" css={rowCss}>
          <StyleCell enabled={isColorEnabled}>Style</StyleCell>
          <StyleCell enabled={isColorEnabled}>
            <select
              value={state.visual}
              data-testid="SelectVisual"
              disabled={!isColorEnabled}
              onChange={(e) => {
                isColorEnabled && commands(ctx).updateVisual(e.target.value);
              }}
            >
              <option value="fill">Fill</option>
              <option value="stroke">Stroke</option>
            </select>
          </StyleCell>
        </div>
        <div className="SelectColor" css={rowCss}>
          <StyleCell enabled={isColorEnabled}>Color</StyleCell>
          <StyleCell enabled={isColorEnabled}>
            <div
              css={css(`
              display: grid;
              align-items: center;
              grid-template-columns: 25px 1fr;
            `)}
            >
              <ColorBox
                enabled={isColorEnabled}
                value={state.color}
                className="d-flex justify-content-around p-0"
                onChange={(color) => {
                  commands(ctx).mutate('color', color);
                }}
              />{' '}
              <div className="d-flex align-content-center">
                <RemoveColorButton />
              </div>
            </div>
          </StyleCell>
        </div>
        {hasErrors && (
          <div data-testid="ErrorRow" css={alertCss}>
            {state.errors.join(' ')}
            <a
              data-testid="DismissButton"
              onClick={() => {
                commands(ctx).reset();
              }}
            >
              Dismiss
            </a>
          </div>
        )}
        {showActionRow && (
          <div className="ActionRow" data-testid="ActionRow" css={rowCss}>
            <div />
            <div css={actionRowCss}>
              {isSaving ? (
                <span data-testid="UpdatingText">Updating...</span>
              ) : (
                <a
                  className="UpdateButton"
                  data-testid="UpdateButton"
                  onClick={onUpdate}
                >
                  Update
                </a>
              )}
              <a
                className="CancelButton"
                data-testid="CancelButton"
                onClick={() => commands(ctx).reset()}
              >
                Cancel
              </a>
            </div>
            <div />
          </div>
        )}
      </div>
    </ReducerContext.Provider>
  );
};

export default Component;
